export const en = {
  brand: "SanchezCloud",
  account: "Account",
  skip: "Skip to main content",
  loading: "Loading your account",
  retry: "Try again",
  save: "Save changes",
  cancel: "Cancel",
  close: "Close",
  copied: "Account ID copied",
  privateNote: "Only you can see and manage these settings.",
  navigation: {
    label: "Account navigation",
    open: "Open navigation",
    overview: "Overview",
    personal: "Personal info",
    security: "Security",
    sessions: "Sessions",
    products: "Connected products",
  },
  auth: {
    welcomeBack: "Welcome back",
    signIn: "Sign in",
    signInDescription: "Sign in to continue.",
    email: "Email address",
    password: "Password",
    forgot: "Forgot password?",
    createPrompt: "New to SanchezCloud?",
    create: "Create an account",
    signingIn: "Signing in",
    genericError: "We couldn't sign you in. Check your details and try again.",
    protectedNote: "Only you can access and manage these settings.",
    recoverTitle: "Reset your password",
    recoverDescription:
      "Enter your sign-in address and we'll send a secure reset link.",
    sendReset: "Send reset link",
    createTitle: "Create your account",
    createDescription: "One account for SanchezCloud products.",
    displayName: "Display name",
    passwordHint: "Use at least 12 characters.",
    emailHelper: "Your SanchezCloud sign-in address",
    passwordHelper: "Enter your SanchezCloud password",
    checkInbox: "Check your inbox",
    checkInboxDescription:
      "If an account matches that address, we sent a secure link with the next step.",
    resetComplete: "Password updated",
    resetCompleteDescription:
      "Your new password is ready. Sign in to continue.",
    verifyPending: "Verifying your email",
    verifyComplete: "Email verified",
    verifyCompleteDescription: "Your account is ready. You can now sign in.",
    verifyInvalid: "This verification link is no longer valid.",
    resetInvalid: "This reset link is missing or no longer valid.",
    confirmPassword: "Confirm new password",
    passwordsMismatch: "The passwords do not match.",
  },
  overview: {
    title: "Account overview",
    description: "Your profile and account activity at a glance.",
    emailVerified: "Verified sign-in email",
    emailUnverified: "Email verification pending",
    manageProfile: "Manage personal info",
    products: "Connected products",
    sessions: "Active sessions",
    sessionCount: (count: number) =>
      `${count} active ${count === 1 ? "session" : "sessions"}`,
    productCount: (count: number) =>
      `${count} connected ${count === 1 ? "product" : "products"}`,
  },
  personal: {
    title: "Personal info",
    description: "Manage the profile used across SanchezCloud products.",
    profile: "Your profile",
    profileDescription: "Each detail appears wherever you use this account.",
    displayName: "Display name",
    displayNameDescription: "Shown in connected SanchezCloud products.",
    email: "Sign-in email",
    emailReadOnly: "Your sign-in email is verified and cannot be changed here.",
    accountId: "Account ID",
    accountIdDescription: "A stable identifier used by SanchezCloud products.",
    copyAccountId: "Copy account ID",
    avatar: "Profile photo",
    avatarDescription: "Choose and crop a photo for your SanchezCloud account.",
    avatarChange: "Change photo",
    avatarRemove: "Remove photo",
    avatarChoose: "Choose photo",
    avatarCrop: "Crop profile photo",
    avatarZoom: "Zoom",
    avatarSave: "Save photo",
    avatarInvalid: "Choose a JPEG, PNG or WebP image smaller than 25 MB.",
    changeName: "Change display name",
    nameUpdated: "Display name updated",
    nameUpdateFailed: "Display name couldn't be updated. Try again.",
    photoUpdated: "Profile photo updated",
    photoUpdateFailed: "Profile photo couldn't be saved. Try again.",
    photoRemoved: "Profile photo removed",
    photoRemoveFailed: "Profile photo couldn't be removed. Try again.",
    removePhotoTitle: "Remove profile photo?",
    removePhotoDescription:
      "Connected products will use your initials until you add another photo.",
    saveName: "Save display name",
    verified: "Verified",
  },
  security: {
    title: "Security",
    description: "Manage your sign-in password.",
    sectionTitle: "Sign-in",
    password: "Password",
    passwordDescription:
      "Changing it signs out every SanchezCloud product session.",
    changePassword: "Change password",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    confirmationTitle: "Change your password?",
    confirmationDescription:
      "Your new password will take effect immediately and other SanchezCloud sessions will be signed out.",
    changed: "Password changed",
    changedDescription: "Your account is now using the new password.",
    changeFailed: "Password couldn't be changed. Check it and try again.",
    mismatch: "The new passwords do not match.",
  },
  sessions: {
    title: "Sessions",
    description: "Review where your SanchezCloud account is signed in.",
    current: "This device",
    other: "Other signed-in sessions",
    signOutOthers: "Sign out others",
    activeNow: "Active now",
    details: "View session details",
    detailTitle: "Session details",
    product: "Product",
    created: "Created",
    lastActive: "Last active",
    expires: "Expires",
    browser: "Browser or device",
    sessionId: "Session ID",
    signOutSession: "Sign out this session",
    confirmTitle: "Sign out all other sessions?",
    confirmDescription:
      "Your current session stays active. Every other SanchezCloud session will be signed out.",
    empty: "No other active sessions",
    emptyDescription: "Your account is not signed in anywhere else.",
    signedOut: "Session signed out",
    othersSignedOut: "Other sessions signed out",
    signOutFailed: "The session couldn't be signed out. Try again.",
    loadError: "Sessions could not be loaded.",
    currentSession: "Current session",
    locationNote: "Location and IP address are not collected.",
  },
  theme: {
    appearance: "Appearance",
    system: "System default",
    light: "Light",
    dark: "Dark",
    reducedMotion: "Reduce motion",
    quickTheme: "Switch color appearance",
  },
  products: {
    title: "Connected products",
    description: "Products signed in with this SanchezCloud account.",
    noProducts: "No connected products yet",
    open: "Open product",
    details: "Product details",
    firstConnected: "First connected",
    lastUsed: "Last used",
    activeSessions: "Active sessions",
    accountAccess: "Account access",
    accountAccessDescription:
      "Uses your display name, profile photo, verified sign-in email and Account ID.",
    loadError: "Connected products could not be loaded.",
    connected: "Connected",
    connectedToday: "Connected · opened today",
    activeLower: "active",
    session: "session",
    sessions: "sessions",
    emptyDescription:
      "Products appear here after you sign in with this SanchezCloud account.",
  },
  help: {
    open: "Help",
    title: "Account help",
    description: "Choose the area where you need help.",
    signIn: "Signing in and password recovery",
    profile: "Profile and account details",
    sessions: "Signed-in sessions",
    signInDescription:
      "Get help with access, verification and password recovery.",
    profileDescription:
      "Understand the account details shared with connected products.",
    sessionsDescription:
      "Review devices and sign out sessions you no longer use.",
  },
  menu: { account: "Account menu", signOut: "Sign out" },
  search: {
    open: "Search account settings",
    title: "Search account settings",
    description: "Search available SanchezCloud Account destinations.",
    placeholder: "Search account settings",
    noResults: "No matching settings",
    noResultsDescription:
      "Try a different word or open a page from navigation.",
  },
  errors: {
    title: "We couldn't load this page",
    description:
      "Try again. If the problem continues, use the request ID when asking for help.",
    requestId: "Request ID",
  },
  notFound: {
    title: "This account page does not exist",
    description: "Open Account overview to continue.",
    action: "Account overview",
  },
} as const;

export const m = en;
